import { Observable, concat, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { chunk } from 'utils';
import {
    ApiTokenPagedResult,
    G2ReviewEnabledUser,
    G2ReviewEnabledUsersParameters,
    G2ReviewEnableResponse
} from 'types';
import { ApiService } from './api.service';
import { Injectable } from '@angular/core';

/** The most user IDs the enable endpoint accepts in one request. */
export const G2_REVIEW_ENABLE_BATCH_SIZE = 100;

export interface G2ReviewEnableBatchOutcome {
    batchNumber: number;
    totalBatches: number;
    userIds: string[];
    /** Undefined when the batch failed. */
    response?: G2ReviewEnableResponse;
}

@Injectable({
    providedIn: 'root'
})
export class AdminG2ReviewsService {
    public constructor(private apiService: ApiService) {}

    public getEnabledUsers(options: G2ReviewEnabledUsersParameters = {}) {
        const path = '/api/admin/v2/g2reviews/enabled';
        return this.apiService.get<ApiTokenPagedResult<G2ReviewEnabledUser[]>>({
            path,
            queryParams: { ...options }
        });
    }

    public enableUsers(userIds: string[]) {
        const path = '/api/admin/v2/g2reviews/enable';
        return this.apiService.post<G2ReviewEnableResponse>({ path, body: { userIds } });
    }

    /**
     * Enables users in sequential batches, trying every batch exactly once. A failed batch does not stop the
     * remaining batches; it is emitted as an outcome with no `response`.
     */
    public enableUsersInBatches(
        userIds: string[],
        batchSize: number = G2_REVIEW_ENABLE_BATCH_SIZE
    ): Observable<G2ReviewEnableBatchOutcome> {
        const batches = chunk(userIds, batchSize);

        return concat(
            ...batches.map((batch, index) =>
                this.enableUsers(batch).pipe(
                    map((response): G2ReviewEnableBatchOutcome => ({
                        batchNumber: index + 1,
                        totalBatches: batches.length,
                        userIds: batch,
                        response
                    })),
                    catchError(() =>
                        of<G2ReviewEnableBatchOutcome>({
                            batchNumber: index + 1,
                            totalBatches: batches.length,
                            userIds: batch
                        })
                    )
                )
            )
        );
    }
}
